/**
 * Xếp hàng MỌI lần tạo task MediaPipe trong cả app. Đây không phải tối ưu, đây là bắt buộc.
 *
 * Loader của tasks-vision 1.0.1 đi qua **biến toàn cục** rồi tự xoá chúng (hàm `qh` trong
 * vision_bundle.mjs, đã rút gọn):
 *
 *     if (e && await $h(e), !self.ModuleFactory) throw Error("ModuleFactory not set.");
 *     ...
 *     i = await self.ModuleFactory(self.Module || i),
 *     self.ModuleFactory = self.Module = void 0,
 *     new t(i, n)
 *
 * `$h()` chèn thẻ `<script>` glue JS, script đó gán `self.ModuleFactory`. Hai lần tạo chồng nhau là
 * hai bên tranh nhau **một** ô biến:
 *
 *   - lần B chèn script → ghi đè `self.ModuleFactory` của lần A;
 *   - lần A `await` xong → gán `self.ModuleFactory = self.Module = void 0`;
 *   - lần B hoặc throw "ModuleFactory not set.", hoặc — tệ hơn — cả hai `new t(i, n)` bọc **cùng
 *     một** Emscripten module. Hai GraphRunner ghi lên một wasm heap thì lỗi không hiện ra lúc
 *     khởi tạo mà hiện ra lúc inference, dưới dạng `Aborted(). Build with -sASSERTIONS for more
 *     info.` — không tên hàm, không stack.
 *
 * App này chồng nhau ở ba chỗ, tất cả đều là đường đi bình thường chứ không phải ca hiếm:
 *
 *   1. fallback GPU→CPU theo đồng hồ: bỏ cuộc GPU **không hủy** được việc đang chạy, nên lúc tạo
 *      bản CPU thì bản GPU vẫn đang bay (chính bản sửa treo-init đã tạo ra ca này);
 *   2. StrictMode mount effect hai lần trong dev: provider thứ hai init khi provider thứ nhất chưa
 *      xong (init mất vài giây, cleanup luôn chạy trước đó);
 *   3. Setup / Calibration / Monitoring mỗi màn dựng provider riêng: đổi màn nhanh là chồng.
 *
 * Vì biến là của `self`, khoá phải ở tầng **module** — không phải tầng provider — để hai provider
 * khác nhau vẫn xếp cùng một hàng.
 */

let tail: Promise<unknown> = Promise.resolve();

/**
 * Chạy `task` sau khi mọi task trước đã settle (kể cả khi chúng reject).
 *
 * Cái giá phải trả: một lần init GPU **treo vô hạn** sẽ chặn hàng đợi, vì không có cách nào hủy
 * việc đang chạy trong wasm và nó vẫn đang giữ biến toàn cục. Đó là lý do
 * `MediaPipeWebVisionProvider` kiểm tra sức khoẻ WebGL2 **trước** khi thử GPU: chặn ca treo từ
 * đầu thì tốt hơn là chạy song song rồi làm hỏng cả hai.
 */
export function serializeMediaPipeCreation<T>(task: () => Promise<T>): Promise<T> {
  const run = tail.then(task, task);
  tail = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}
